import * as React from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { ArrowRightIcon } from '@heroicons/react/24/solid';

import { cn, SadFaceIcon, useResponsiveBreakPoints, getDurationSinceDate } from '@restworld/utils-common';
import { BuildingOffice2Icon } from '@heroicons/react/24/outline';
import { GetMissedOrgApplicationActivitiesCategory } from '@restworld/data-services';
import { EmployerCandidate, NewCandidateNotification } from '../new-candidate-notification';
import { V2Link } from '../v2-link';
import { Heading } from '@restworld/ui-ds-base';

export function EmployerActivityCard(props: EmployerActivityCardProps) {
  const {
    candidateCount,
    restaurantName,
    city,
    jobPositionTitle,
    dateSinceLastActivity,
    href,
    candidates = [],
    isFirst = true,
    isLast = true,
    variant = 'new_shortlist_applications',
  } = props;
  const { t } = useTranslation('common');
  const headingEl = useActivityCardHeading({ variant, candidateCount, jobPositionTitle, restaurantName, candidates });
  const [isHovered, setIsHovered] = useState(false);
  const { isMobile } = useResponsiveBreakPoints();

  const { duration: timeSinceLastActivity, unit } = useMemo(
    () => getDurationSinceDate(dateSinceLastActivity),
    [dateSinceLastActivity]
  );

  const timeAgoText = useMemo(
    () =>
      timeSinceLastActivity > 1
        ? t(
            unit === 'months'
              ? 'months_key'
              : unit === 'days'
              ? 'days_key'
              : unit === 'hours'
              ? 'hours_key'
              : 'minutes_key'
          )
        : t(
            unit === 'months' ? 'month_key' : unit === 'days' ? 'day_key' : unit === 'hours' ? 'hour_key' : 'minute_key'
          ),
    [timeSinceLastActivity, t, unit]
  );

  const candidateDisplayMax = 3;
  const currentCandidateCount = useMemo(
    () => (candidateCount <= candidateDisplayMax ? candidateCount : candidateDisplayMax),
    [candidateCount]
  );
  const currentCandidates = useMemo(() => candidates.slice(0, candidateDisplayMax), [candidates]);

  return (
    <V2Link href={href || ''}>
      <MainWrapper
        isLast={isLast}
        isFirst={isFirst}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        isHovered={isHovered}
      >
        <ContentWrapper>
          <TitleWrapper>{headingEl}</TitleWrapper>
          <BottomContentWrapper>
            <div>
              {variant === 'active_applications_without_feedback' ? (
                <SadFaceIconWrapper>
                  <SadFaceIcon className="fill-red-700" />
                </SadFaceIconWrapper>
              ) : (
                <NewCandidateNotification
                  wrapperStyle="bg-transparent w-16 min-w-[64px]"
                  imageWrapperStyle={variant === 'spontaneous_applications' ? 'bg-orange-700' : 'bg-purple-700'}
                  candidateCount={currentCandidateCount}
                  candidates={currentCandidates}
                />
              )}
            </div>
            <span className="flex items-center gap-1 text-sm text-neutral-100 font-normal">
              <BuildingOffice2Icon className="h-4 w-4" />
              {variant === 'spontaneous_applications' ? city : `${restaurantName}, ${city}`}
            </span>
          </BottomContentWrapper>
        </ContentWrapper>
        <StatusWrapper>
          {isHovered && !isMobile ? (
            <ArrowRightIcon className="h-6 w-6 text-neutral-700" />
          ) : (
            <>
              <span className="text-sm text-neutral-100 font-normal">
                {timeSinceLastActivity} {timeAgoText} {t('ago')}
              </span>
              <Indicator variant={variant === 'active_applications_without_feedback' ? 'error' : 'default'} />
            </>
          )}
        </StatusWrapper>
      </MainWrapper>
    </V2Link>
  );
}
export default EmployerActivityCard;

export interface EmployerActivityCardProps {
  variant?: GetMissedOrgApplicationActivitiesCategory;
  candidates: EmployerCandidate[];
  candidateCount: number;
  restaurantName: string;
  city: string;
  jobPositionTitle: string;
  dateSinceLastActivity: string;
  isLast?: boolean;
  isFirst?: boolean;
  href?: string;
}

function useActivityCardHeading({
  variant,
  candidateCount,
  jobPositionTitle,
  restaurantName,
  candidates,
}: Pick<
  EmployerActivityCardProps,
  'variant' | 'candidateCount' | 'jobPositionTitle' | 'restaurantName' | 'candidates'
>) {
  const { t } = useTranslation('common');
  const isSingleCandidate = candidateCount === 1;

  const defaultVariant = useMemo(
    () => (
      <>
        {t(isSingleCandidate ? 'there_is_key' : 'there_are')}{' '}
        <PurpleText>
          {candidateCount} {t(isSingleCandidate ? 'new_candidate_key' : 'new_candidates')}
        </PurpleText>{' '}
        {t('in_the_shortlist_for')} <NeutralText>{jobPositionTitle}</NeutralText>{' '}
        <span role="img" aria-label="party popper">
          🎉
        </span>
      </>
    ),
    [candidateCount, isSingleCandidate, jobPositionTitle, t]
  );

  const spontaneousVariant = useMemo(
    () => (
      <>
        <NeutralText>
          {candidateCount} {t(isSingleCandidate ? 'new_spontaneous_candidate' : 'new_spontaneous_candidates')}
        </NeutralText>{' '}
        {t('applied_to_work_at')} <NeutralText>{restaurantName}</NeutralText>{' '}
        <span role="img" aria-label="eyes">
          👀
        </span>
      </>
    ),
    [candidateCount, isSingleCandidate, restaurantName, t]
  );

  const multipleCandidatesVariantText = useMemo(
    () => (
      <>
        {t('and')} <NeutralText>{t('a_number_of_others', { count: candidateCount - 1 })}</NeutralText>{' '}
        {t('are_waiting_for_your_feedback')}
      </>
    ),
    [candidateCount, t]
  );
  const singleCandidateVariantText = useMemo(() => t('is_waiting_for_your_feedback'), [t]);
  const noNameVariantText = useMemo(
    () =>
      t(isSingleCandidate ? 'a_candidate_is_waiting_for_your_feedback' : 'candidates_are_waiting_for_your_feedback', {
        count: candidateCount,
      }),
    [candidateCount, isSingleCandidate, t]
  );

  const lastCheckLongAgoVariant = useMemo(() => {
    const nameOfCandidate = candidates?.length ? `${candidates[0]?.firstName} ${candidates[0]?.lastName}` : '';

    return (
      <>
        {t('you_havent_checked_the_position')} <NeutralText>{jobPositionTitle}</NeutralText> {t('for_a_while')},{' '}
        <NeutralText>{nameOfCandidate}</NeutralText>{' '}
        {!nameOfCandidate
          ? noNameVariantText
          : isSingleCandidate
          ? singleCandidateVariantText
          : multipleCandidatesVariantText}
      </>
    );
  }, [
    candidates,
    isSingleCandidate,
    jobPositionTitle,
    multipleCandidatesVariantText,
    noNameVariantText,
    singleCandidateVariantText,
    t,
  ]);

  const currentVariant = useMemo(
    () =>
      variant === 'spontaneous_applications'
        ? spontaneousVariant
        : variant === 'active_applications_without_feedback'
        ? lastCheckLongAgoVariant
        : defaultVariant,
    [defaultVariant, lastCheckLongAgoVariant, spontaneousVariant, variant]
  );

  return (
    <Heading as="span" size={5} weight="semibold" color="neutral-light" className="line-clamp-3 md:line-clamp-2">
      {currentVariant}
    </Heading>
  );
}

const MainWrapper = ({ isFirst, isLast, isHovered, className, ...props }: any) => (
  <div
    className={cn(
      'w-full flex flex-col-reverse p-4 pb-6 border-neutral-40 border-b transition-all md:flex-row md:items-center md:justify-between md:gap-5 md:p-8',
      isFirst ? 'rounded-t-lg' : '',
      isLast ? 'rounded-b-lg border-b-0' : '',
      isHovered ? 'bg-neutral-20' : 'bg-neutral-10',
      className
    )}
    {...props}
  />
);
const ContentWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex-[4]', className)} {...props} />
);
const StatusWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn('flex-1 flex items-center gap-3 justify-between mb-4 md:mb-0 md:justify-end', className)}
    {...props}
  />
);
const PurpleText = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <span className={cn('text-purple-700', className)} {...props} />
);
const NeutralText = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <span className={cn('text-neutral-700', className)} {...props} />
);
const Indicator = ({ variant, className, ...props }: any) => (
  <div
    className={cn('h-3 w-3 rounded-full', variant === 'error' ? 'bg-red-500' : 'bg-purple-500', className)}
    {...props}
  />
);
const TitleWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('mb-4 md:mb-5', className)} {...props} />
);
const BottomContentWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex gap-3', className)} {...props} />
);
const SadFaceIconWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('w-12', className)} {...props} />
);
